// FILE: src/components/MilestonesPage.js
//
// Phase E2 — full Milestones page (view='milestones'). Earned + locked
// achievements organized by tier. The lightweight RecentMilestones strip
// stays on the dashboard as a preview; this is the full catalog.
//
// Voice (§6.1): coaching, never gamified. No XP, no levels. Locked
// achievements show their description as an invitation, never as a chore.

import React, { useEffect, useMemo, useState } from 'react';
import styles from './MilestonesPage.module.css';
import ScreenHeader from '../shared/ScreenHeader';
import DiscordPromo from '../shared/DiscordPromo';
import AchievementBadge from './AchievementBadge';
import BadgeLightbox from './BadgeLightbox';
import FoundingTesterBadge from '../founding-testers/FoundingTesterBadge';
import ExamPrepChecklist from '../studyplan/ExamPrepChecklist';
import CertTrackSwitcher from '../certification/CertTrackSwitcher';
import LearnerDashboardEmpty from '../dashboard/LearnerDashboardEmpty';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { loadMilestonesSummary } from '../../services/achievementService';
import { loadPrepTasks } from '../../services/prepTasksService';
import { getAllExams, getExamConfig, getBaselineColumn } from '../../config/exams';

const ACTIVE_CERTS = getAllExams().filter((e) => !e.comingSoon).map((e) => e.examId);

const TIER_LABEL = {
  foundation: 'Foundation',
  progress:   'Progress',
  mastery:    'Mastery',
  completion: 'Completion',
};
const TIER_ORDER = ['foundation', 'progress', 'mastery', 'completion'];

export default function MilestonesPage({ onNavigate, onStartCert }) {
  const [loading, setLoading] = useState(true);
  const [byKey, setByKey]     = useState({});
  const [earnedCount, setEarnedCount] = useState(0);
  const { profile } = useLearnerAuth();

  // Per-cert state: which track is in focus, baseline status, prep-task progress.
  const [activeCert, setActiveCert] = useState(null);
  const [prepByCert, setPrepByCert] = useState({});
  const [baselineDialogOpen, setBaselineDialogOpen] = useState(false);
  const [zoomItem, setZoomItem] = useState(null);

  const baselines = useMemo(() => {
    const out = {};
    for (const c of ACTIVE_CERTS) out[c] = profile?.[getBaselineColumn(c)] || null;
    return out;
  }, [profile]);

  // Default the focused track to the learner's target cert, else the first
  // cert with a baseline, else the first active cert.
  useEffect(() => {
    if (activeCert) return;
    const withBaseline = ACTIVE_CERTS.filter((c) => baselines[c]);
    const init =
      (profile?.target_exam_id && ACTIVE_CERTS.includes(profile.target_exam_id))
        ? profile.target_exam_id
        : (withBaseline[0] || ACTIVE_CERTS[0]);
    setActiveCert(init);
  }, [profile, baselines, activeCert]);

  // Load prep-task completion counts for each active cert (per-cert summary).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = {};
      for (const c of ACTIVE_CERTS) {
        const targetDate = profile?.target_exam_id === c ? (profile?.target_exam_date || null) : null;
        const tasks = await loadPrepTasks(c, { targetExamDate: targetDate });
        out[c] = { total: tasks.length, done: tasks.filter((t) => t.completed).length };
      }
      if (!cancelled) setPrepByCert(out);
    })();
    return () => { cancelled = true; };
  }, [profile?.target_exam_id, profile?.target_exam_date]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const summary = await loadMilestonesSummary();
      if (cancelled) return;
      setByKey(summary.byKey || {});
      setEarnedCount((summary.earned || []).length);
      setLoading(false);
    })();
    function onEarned() {
      (async () => {
        const summary = await loadMilestonesSummary();
        if (!cancelled) {
          setByKey(summary.byKey || {});
          setEarnedCount((summary.earned || []).length);
        }
      })();
    }
    window.addEventListener('pm-pixel:achievement-earned', onEarned);
    return () => {
      cancelled = true;
      window.removeEventListener('pm-pixel:achievement-earned', onEarned);
    };
  }, []);

  const tiers = {};
  for (const key of Object.keys(byKey)) {
    const item = byKey[key];
    const tier = item.tier || 'progress';
    if (!tiers[tier]) tiers[tier] = [];
    tiers[tier].push(item);
  }

  const totalCount = Object.keys(byKey).length;

  // Certification earned is the summit of the journey (interaction map #8).
  // When that milestone is actually earned, surface PM Pixel's `certified`
  // pose once at the top of the page — a genuine, earned celebration, kept
  // separate from the deliberately-neutral outcome-recording form (D3).
  const certEarned = byKey.certification_earned?.earned === true;

  // The page header text. When the certification is actually earned, this same
  // content is paired with PM Pixel's `certified` pose in the reusable coach
  // layout (avatar left, message right) — a genuine, earned celebration kept
  // separate from the deliberately-neutral outcome-recording form (D3).
  const headerSubtitle = (
    <>
      <p className={styles.lead}>
        Coaching markers along your journey. Each one is a concrete moment
        worth noticing — never XP, never levels, never a number to defend.
      </p>
      {!loading && (
        <p className={styles.counter} data-testid="milestones-counter">
          {earnedCount} of {totalCount} earned
        </p>
      )}
      <div style={{ marginTop: 14 }}>
        <FoundingTesterBadge auto size={44} />
      </div>
    </>
  );

  return (
    <div className={styles.page} data-testid="milestones-page">
      {/* ── Standard topmast (ScreenHeader banner). When the certification is
          earned, PM Pixel's `certified` pose rides the banner — the same
          earned celebration, now in the topmast. ── */}
      <ScreenHeader
        variant="banner"
        coachPose={!loading && certEarned ? 'certified' : undefined}
        onBack={() => onNavigate?.('dashboard')}
        backTestId="milestones-back"
        eyebrow="Your Certification Journey"
        title="Milestones"
        subtitle={headerSubtitle}
        promo={<DiscordPromo />}
        testId={!loading && certEarned ? 'milestones-cert-celebration' : undefined}
      />

      {/* Active certification tracks — switch focus or start a new one. */}
      <CertTrackSwitcher
        activeCert={activeCert}
        baselines={baselines}
        onSelect={setActiveCert}
        onStartCert={() => setBaselineDialogOpen(true)}
        testIdPrefix="milestones"
      />

      {/* Per-cert summary — all active tracks at a glance before drilling in. */}
      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 12, margin: '0 0 1.25rem' }}
        data-testid="milestones-cert-summary"
      >
        {ACTIVE_CERTS.map((c) => {
          const cfg = getExamConfig(c);
          const label = cfg?.displayName || c.toUpperCase();
          const hasBaseline = !!baselines[c];
          const prep = prepByCert[c];
          const selected = activeCert === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => (hasBaseline ? setActiveCert(c) : setBaselineDialogOpen(true))}
              data-testid={`milestones-summary-${c}`}
              style={{
                textAlign: 'left', cursor: 'pointer', minWidth: 180, flex: '1 1 180px',
                border: selected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                borderRadius: 10, padding: '0.75rem 0.9rem',
                background: selected ? '#f8fbff' : '#fff',
              }}
            >
              <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                {hasBaseline ? 'Baseline set' : 'Not started yet'}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                {prep ? `Prep tasks: ${prep.done}/${prep.total} complete` : 'Prep tasks: —'}
              </div>
            </button>
          );
        })}
      </div>

      {/* Self-attested exam-prep checklist for the focused cert (training hours,
          application, scheduling) — distinct from the earned-milestone grid. */}
      <ExamPrepChecklist examId={activeCert || 'pmp'} />

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : (
        TIER_ORDER.map((tier) => {
          const items = tiers[tier] || [];
          if (items.length === 0) return null;
          // Sort within tier: earned first by earned_at desc, then unearned by key
          items.sort((a, b) => {
            if (a.earned !== b.earned) return a.earned ? -1 : 1;
            if (a.earned && b.earned) return new Date(b.earned_at) - new Date(a.earned_at);
            return (a.key || '').localeCompare(b.key || '');
          });
          return (
            <section key={tier} className={styles.tierSection} data-testid={`milestones-tier-${tier}`}>
              <h2 className={styles.tierHeading}>{TIER_LABEL[tier]}</h2>
              <ul className={styles.grid}>
                {items.map((item) => (
                  <li key={item.key} className={`${styles.card} ${item.earned ? styles.earned : styles.locked}`}
                    data-testid={`milestones-card-${item.key}`}
                  >
                    <div className={styles.cardRow}>
                      <button
                        type="button"
                        className={styles.badgeButton}
                        onClick={() => setZoomItem(item)}
                        aria-label={`View ${item.title} badge larger`}
                        data-testid={`milestones-badge-zoom-${item.key}`}
                      >
                        <AchievementBadge
                          iconKey={item.icon}
                          earned={!!item.earned}
                          title={item.title}
                          size={96}
                        />
                      </button>
                      <div className={styles.cardBody}>
                        <div className={styles.cardHeader}>
                          <span className={styles.cardTitle}>{item.title}</span>
                          {item.earned && <span className={styles.earnedBadge} data-testid="earned-badge">Earned</span>}
                        </div>
                        <p className={styles.cardDesc}>{item.description}</p>
                        {item.earned && item.earned_at && (
                          <p className={styles.cardDate}>{formatDate(item.earned_at)}</p>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}

      {/* "Set a <cert> baseline" dialog — opened from the track switcher /
          summary when the learner picks a cert they haven't started. */}
      {/* Click-to-zoom badge viewer. */}
      <BadgeLightbox item={zoomItem} onClose={() => setZoomItem(null)} />

      <LearnerDashboardEmpty
        isOpen={baselineDialogOpen}
        onClose={() => setBaselineDialogOpen(false)}
        onStartCert={onStartCert}
        baselines={baselines}
        displayName={profile?.display_name || null}
      />
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) { return iso; }
}
